import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ThumbsUp, X, RefreshCw, Users, MapPin, MessageCircle, Search, UserPlus, UserMinus } from 'lucide-react';
import { StrokesToParBadge } from '../components/StrokesToParBadge';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import { TierPill, TIER_STYLES, getUserTier } from '../components/TierBadge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREAS = [
  { value: "all", label: "All Areas" },
  { value: "Gainesville", label: "Gainesville, FL" },
  { value: "Orlando", label: "Orlando / Bay Hill" },
  { value: "Tampa", label: "Tampa Bay" },
  { value: "Miami", label: "Miami / South FL" },
];

const getDefaultPhoto = (name) => `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'G')}&background=0F3B2E&color=fff&size=400&font-size=0.4&bold=true`;
const getPhotoUrl = (url) => url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const Discover = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [golfers, setGolfers] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [swiping, setSwiping] = useState(null);
  const [matchModal, setMatchModal] = useState(null);
  const [selectedArea, setSelectedArea] = useState("all");

  // Search state
  const [searchMode, setSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [followLoading, setFollowLoading] = useState({});
  const [dmLoading, setDmLoading] = useState({});

  // Swipe gesture state
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);

  const handleTouchStart = (e) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    isDraggingRef.current = false;
  };

  const handleTouchMove = (e) => {
    const dx = e.touches[0].clientX - touchStartRef.current.x;
    const dy = Math.abs(e.touches[0].clientY - touchStartRef.current.y);
    if (Math.abs(dx) > dy && Math.abs(dx) > 10) {
      isDraggingRef.current = true;
      setDragging(true);
      setDragX(dx);
    }
  };

  const handleTouchEnd = () => {
    if (isDraggingRef.current) {
      if (dragX > 80) {
        handleSwipe('like');
      } else if (dragX < -80) {
        handleSwipe('pass');
      }
    }
    setDragX(0);
    setDragging(false);
    isDraggingRef.current = false;
  };

  const handleMouseDown = (e) => {
    touchStartRef.current = { x: e.clientX, y: e.clientY };
    isDraggingRef.current = false;
    const onMove = (ev) => {
      const dx = ev.clientX - touchStartRef.current.x;
      if (Math.abs(dx) > 10) {
        isDraggingRef.current = true;
        setDragging(true);
        setDragX(dx);
      }
    };
    const onUp = () => {
      if (isDraggingRef.current) {
        if (dragX > 80) handleSwipe('like');
        else if (dragX < -80) handleSwipe('pass');
      }
      setDragX(0);
      setDragging(false);
      isDraggingRef.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const fetchGolfers = useCallback(async (area) => {
    try {
      setLoading(true);
      const params = area && area !== "all" ? `?area=${encodeURIComponent(area)}` : "";
      const res = await axios.get(`${API}/discover${params}`);
      setGolfers(res.data);
      setCurrentIndex(0);
    } catch {
      toast.error('Failed to load golfers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGolfers(selectedArea); }, [fetchGolfers, selectedArea]);

  const handleSwipe = async (action) => {
    if (currentIndex >= golfers.length) return;
    const currentGolfer = golfers[currentIndex];
    setSwiping(action);
    try {
      const response = await axios.post(`${API}/swipe`, { target_user_id: currentGolfer.id, action });
      if (response.data.is_match) setMatchModal(response.data.match);
      setTimeout(() => { setSwiping(null); setCurrentIndex(prev => prev + 1); }, 300);
    } catch {
      toast.error('Something went wrong');
      setSwiping(null);
    }
  };

  // Search functions
  const searchUsers = useCallback(async (q) => {
    if (!q || q.length < 1) { setSearchResults([]); return; }
    try {
      setSearching(true);
      const res = await axios.get(`${API}/search?q=${encodeURIComponent(q)}`);
      setSearchResults(res.data);
    } catch { /* silent */ }
    finally { setSearching(false); }
  }, []);

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (val.length > 0 && !searchMode) setSearchMode(true);
    if (val.length === 0) { setSearchMode(false); setSearchResults([]); return; }
    clearTimeout(window._searchTimeout);
    window._searchTimeout = setTimeout(() => searchUsers(val), 300);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchMode(false);
    setSearchResults([]);
  };

  const toggleFollow = async (userId, isFollowing) => {
    setFollowLoading(prev => ({ ...prev, [userId]: true }));
    try {
      if (isFollowing) {
        await axios.delete(`${API}/follow/${userId}`);
        toast.success('Unfollowed');
      } else {
        await axios.post(`${API}/follow/${userId}`);
        toast.success('Following!');
      }
      setSearchResults(prev => prev.map(u =>
        u.id === userId ? { ...u, is_following: !isFollowing } : u
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setFollowLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const startDM = async (userId) => {
    setDmLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const res = await axios.post(`${API}/conversations/start`, { user_id: userId });
      navigate(`/messages/${res.data.conversation_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start conversation');
    } finally {
      setDmLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const currentGolfer = golfers[currentIndex];
  const paceLabels = { ready_golf: 'Ready Golf', quick: 'Quick', average: 'Average', deliberate: 'Deliberate', very_deliberate: 'Very Deliberate', very_fast: 'Fast', fast: 'Fast', moderate: 'Average', slow: 'Deliberate', very_slow: 'Very Deliberate' };
  const rideLabels = { walk: 'Walker', ride: 'Rider', either: 'Either' };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="discover-page">
      {/* Header */}
      <div className="px-4 py-3 sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="flex items-center justify-between mb-2">
          <h1 className="font-serif text-2xl font-bold text-foreground">Discover</h1>
          {!searchMode && (
            <div className="flex items-center gap-2">
              <Select value={selectedArea} onValueChange={setSelectedArea}>
                <SelectTrigger className="w-[140px] h-9 rounded-full text-xs" data-testid="area-select">
                  <MapPin className="w-3.5 h-3.5 mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AREAS.map(a => (
                    <SelectItem key={a.value} value={a.value} data-testid={`area-option-${a.value}`}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon" onClick={() => fetchGolfers(selectedArea)} className="rounded-full" data-testid="discover-refresh-btn">
                <RefreshCw className="w-5 h-5 text-muted-foreground" />
              </Button>
            </div>
          )}
          {searchMode && (
            <Button variant="ghost" size="sm" onClick={clearSearch} className="text-xs rounded-full" data-testid="clear-search-btn">
              Cancel
            </Button>
          )}
        </div>
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search golfers by name..."
            value={searchQuery}
            onChange={handleSearchInput}
            className="pl-10 h-10 rounded-xl"
            data-testid="user-search-input"
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-4">
        {/* SEARCH MODE */}
        {searchMode ? (
          <div data-testid="search-results-section">
            {searching ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse flex items-center gap-3 p-3 rounded-xl bg-muted">
                    <div className="w-12 h-12 rounded-full bg-muted-foreground/20" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 rounded w-1/3 bg-muted-foreground/20" />
                      <div className="h-3 rounded w-1/2 bg-muted-foreground/20" />
                    </div>
                  </div>
                ))}
              </div>
            ) : searchResults.length === 0 && searchQuery.length > 0 ? (
              <div className="text-center py-12">
                <Search className="w-10 h-10 mx-auto mb-3 text-muted" />
                <p className="text-sm text-muted-foreground">No golfers found for "{searchQuery}"</p>
              </div>
            ) : (
              <div className="space-y-2">
                {searchResults.map(u => (
                  <div
                    key={u.id}
                    className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card transition-colors"
                    data-testid={`search-result-${u.id}`}
                  >
                    <Avatar
                      className="w-12 h-12 border-2 border-border cursor-pointer"
                      onClick={() => navigate(`/user/${u.id}`)}
                    >
                      <AvatarImage src={getPhotoUrl(u.profile_photo) || getDefaultPhoto(u.display_name)} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {u.display_name?.charAt(0) || 'G'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/user/${u.id}`)}>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <h3 className="font-semibold text-sm truncate text-foreground">{u.display_name || u.username}</h3>
                        {getUserTier(u) && <TierPill tier={getUserTier(u)} size="xs" />}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {u.location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {u.location}</span>}
                        {u.display_metric?.value != null ? (
                          <StrokesToParBadge display_metric={u.display_metric} size="sm" showLabel={false} showInfo={false} />
                        ) : u.handicap != null && <span>{u.handicap} Hdcp</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button
                        size="sm" variant="ghost" className="rounded-full text-xs h-8 w-8 p-0"
                        onClick={() => startDM(u.id)} disabled={dmLoading[u.id]}
                        data-testid={`dm-btn-${u.id}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm" variant={u.is_following ? "outline" : "default"}
                        className="rounded-full text-xs gap-1 h-8 px-3"
                        onClick={() => toggleFollow(u.id, u.is_following)}
                        disabled={followLoading[u.id]}
                        data-testid={`follow-btn-${u.id}`}
                      >
                        {u.is_following ? <><UserMinus className="w-3.5 h-3.5" /> Following</> : <><UserPlus className="w-3.5 h-3.5" /> Follow</>}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* DISCOVER MODE — swipe cards */
          <>
            {loading ? (
              <div className="animate-pulse"><div className="h-[480px] rounded-3xl bg-muted" /></div>
            ) : !currentGolfer ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center bg-muted">
                  <Users className="w-10 h-10 text-muted-foreground" />
                </div>
                <h3 className="font-serif text-xl font-bold mb-2 text-foreground">No More Golfers</h3>
                <p className="text-sm mb-4 text-muted-foreground">Check back later or adjust your preferences</p>
                <Button onClick={() => fetchGolfers(selectedArea)} className="rounded-full gap-2" data-testid="discover-reload-btn">
                  <RefreshCw className="w-4 h-4" /> Refresh
                </Button>
              </div>
            ) : (
              <div
                className={`transition-all ${!dragging ? 'duration-300' : 'duration-0'} ${swiping === 'like' ? 'translate-x-[120%] opacity-0 rotate-12' : swiping === 'pass' ? '-translate-x-[120%] opacity-0 -rotate-12' : ''}`}
                style={dragging ? { transform: `translateX(${dragX}px) rotate(${dragX * 0.05}deg)`, opacity: 1 - Math.abs(dragX) / 400 } : undefined}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={handleMouseDown}
              >
                {/* Swipe indicators */}
                {dragging && dragX > 40 && (
                  <div className="absolute top-8 left-8 z-20 px-4 py-2 rounded-xl border-4 border-green-500 text-green-500 text-2xl font-black -rotate-12 bg-card/80">LIKE</div>
                )}
                {dragging && dragX < -40 && (
                  <div className="absolute top-8 right-8 z-20 px-4 py-2 rounded-xl border-4 border-red-500 text-red-500 text-2xl font-black rotate-12 bg-card/80">PASS</div>
                )}
                <div className="rounded-3xl overflow-hidden border border-border shadow-md bg-card" data-testid="discover-card">
                  <div className="relative h-80">
                    {getUserTier(currentGolfer) && (
                      <div className="absolute inset-0 z-[1] pointer-events-none rounded-t-3xl" style={{ boxShadow: `inset 0 0 0 4px ${currentGolfer.is_founder ? '#D4AF5A' : '#B8C4CF'}` }} />
                    )}
                    <img
                      src={getPhotoUrl(currentGolfer.profile_photo) || getDefaultPhoto(currentGolfer.display_name)}
                      alt={currentGolfer.display_name}
                      className="w-full h-full object-cover"
                    />
                    {getUserTier(currentGolfer) && (
                      <div className="absolute top-3 right-3 z-10"><TierPill tier={getUserTier(currentGolfer)} size="sm" /></div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                      <div className="flex items-center gap-2">
                        <h2 className="text-2xl font-bold text-white">{currentGolfer.display_name}, {currentGolfer.age}</h2>
                      </div>
                      <div className="flex items-center gap-1 text-white/80 text-sm mt-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {currentGolfer.location || 'Unknown location'}
                      </div>
                    </div>
                  </div>

                  <div className="p-5 space-y-4">
                    <div className="flex flex-wrap gap-2">
                      {currentGolfer.display_metric?.value != null ? (
                        <StrokesToParBadge display_metric={currentGolfer.display_metric} strokes_to_par={currentGolfer.strokes_to_par} />
                      ) : currentGolfer.handicap != null && (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">
                          {currentGolfer.handicap_official ? 'Official' : 'Est.'} {currentGolfer.handicap} Hdcp
                        </span>
                      )}
                      {currentGolfer.playing_pace && (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-muted text-muted-foreground">
                          {paceLabels[currentGolfer.playing_pace] || currentGolfer.playing_pace} Pace
                        </span>
                      )}
                      {currentGolfer.walking_or_riding && (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-muted text-muted-foreground">
                          {rideLabels[currentGolfer.walking_or_riding] || currentGolfer.walking_or_riding}
                        </span>
                      )}
                      {currentGolfer.is_private_club_member && (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-secondary/15 text-secondary">Private Club</span>
                      )}
                      {currentGolfer.plays_music && (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-muted text-muted-foreground">Music on Course</span>
                      )}
                      {currentGolfer.drinks_on_course === 'yes' && (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-muted text-muted-foreground">Drinks</span>
                      )}
                      {currentGolfer.smokes_on_course === 'yes' && (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-muted text-muted-foreground">Smokes</span>
                      )}
                      {currentGolfer.plays_for_money && (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-secondary/15 text-secondary">Plays for $</span>
                      )}
                    </div>
                    {currentGolfer.bio && <p className="text-sm leading-relaxed text-foreground">{currentGolfer.bio}</p>}
                    {currentGolfer.availability?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {currentGolfer.availability.map(a => (
                          <span key={a} className="px-2.5 py-1 rounded-full text-[10px] font-semibold capitalize bg-muted text-muted-foreground">{a}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-center gap-6 mt-6">
                  <button onClick={() => handleSwipe('pass')} className="w-16 h-16 rounded-full flex items-center justify-center shadow-md transition-all active:scale-90 border-2 bg-card border-border" data-testid="swipe-pass-btn">
                    <X className="w-7 h-7 text-muted-foreground" />
                  </button>
                  <button onClick={() => handleSwipe('like')} className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg transition-all active:scale-90 bg-primary" data-testid="swipe-like-btn">
                    <ThumbsUp className="w-9 h-9 text-primary-foreground" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Match Modal */}
      <Dialog open={!!matchModal} onOpenChange={() => setMatchModal(null)}>
        <DialogContent className="max-w-sm rounded-2xl text-center p-8">
          <h2 className="font-serif text-2xl font-bold mb-2 text-foreground">It's a Match!</h2>
          <p className="text-sm mb-6 text-muted-foreground">
            You and <span className="font-semibold">{matchModal?.matched_user?.display_name}</span> both liked each other!
          </p>
          <div className="flex flex-col gap-2">
            <Button onClick={async () => {
              const uid = matchModal?.matched_user?.id;
              if (uid) {
                try { await axios.post(`${API}/follow/${uid}`); } catch { /* silent */ }
              }
              setMatchModal(null);
              navigate(`/messages/${matchModal?.match_id}`);
            }} className="w-full rounded-full gap-2" data-testid="match-send-message-btn">
              <MessageCircle className="w-4 h-4" /> Follow & Message
            </Button>
            <Button variant="outline" onClick={() => {
              const uid = matchModal?.matched_user?.id;
              if (uid) {
                try { axios.post(`${API}/follow/${uid}`); } catch { /* silent */ }
              }
              setMatchModal(null);
            }} className="w-full rounded-full gap-2" data-testid="match-follow-btn">
              <UserPlus className="w-4 h-4" /> Follow
            </Button>
            <Button variant="ghost" onClick={() => setMatchModal(null)} className="w-full rounded-full text-muted-foreground text-sm" data-testid="match-later-btn">
              Maybe Later
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Navigation />
    </div>
  );
};

export default Discover;
